import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'
import Script from 'next/script'

import config from '@config'
import { isVideoOnlyArticle } from '@utils/cook'
import { calculateSSOTrackingSource } from '@utils/sso'
import { getURLParam } from '@utils/urlParams'
import useAppTheme from '@hooks/useAppTheme'
import useCMPCookieCategories from '@hooks/useCMPCookieCategories'
import useExecuteOnClientNavigation, {
  UseExecuteOnClientNavigation,
} from '@hooks/useExecuteOnClientNavigation'
import useIsPianoDebugModeActive from '@hooks/useIsPianoDebugModeActive'
import usePageMetadata, { PageMetadata } from '@hooks/usePageMetadata'
import useSubscriptionStatus from '@hooks/useSubscriptionStatus'
import useUser from '@hooks/useUser'

import { initializePiano, updateExperience } from './skeleton'
import { PianoEvents } from './types'
import usePianoEvents from './usePianoEvents'

const {
  backend: { hotModuleReloading },
  piano: { pianoSdkUrl, pianoARIScriptUrl },
  plusLinkShare: { linkConsumptionApiUrl },
} = config

type InitializePianoAsyncFn = ({
  queryClient,
  articleId,
  ssoSource,
  isSubscribed,
  isPlus,
  isPromo,
  isLoggedIn,
  isDebugMode,
  isVideoOnly,
  isLivescore,
  pianoEvents,
  contentOrigin,
}: {
  queryClient: QueryClient
  articleId: PageMetadata['id']
  ssoSource: string
  isSubscribed: boolean
  isPlus: boolean
  isPromo: boolean
  isLoggedIn: boolean
  isDebugMode: boolean
  isVideoOnly: boolean
  isLivescore: boolean
  pianoEvents: PianoEvents
  contentOrigin: string
}) => Promise<void>

const initializePianoAsync: InitializePianoAsyncFn = async ({
  queryClient,
  articleId,
  ssoSource,
  isSubscribed,
  isPlus,
  isPromo,
  isLoggedIn,
  isDebugMode,
  isVideoOnly,
  isLivescore,
  pianoEvents,
  contentOrigin,
}) => {
  initializePiano({
    isLoggedIn,
    currentUrl: `${window.location.href}`,
    ssoSource,
    pianoEvents,
    isPlus,
    isPromo,
    isSubscribed,
    isVideoOnly,
    isLivescore,
    isGifted:
      !isSubscribed &&
      !!articleId &&
      (await fetchIsGifted(articleId, queryClient)),
    isDebugMode,
    queryClient,
    articleId,
    contentOrigin,
  })

  queryClient.setQueryData(['isPianoSDKLoaded'], true)
  queryClient.invalidateQueries({
    queryKey: ['isPianoSDKLoaded'],
    exact: true,
  })
}

const fetchIsGifted = async (
  articleId: PageMetadata['id'],
  queryClient: QueryClient,
) => {
  const sharingPlusQueryParam = getURLParam('sharingPlus', queryClient)
  if (sharingPlusQueryParam) {
    const response = await fetch(linkConsumptionApiUrl, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        articleId,
        sharingCode: sharingPlusQueryParam,
      }),
    })
    if (response.ok) {
      queryClient.setQueryData(['isGiftedArticle'], true)
      queryClient.invalidateQueries({
        queryKey: ['isGiftedArticle'],
        exact: true,
      })
      return true
    }
  }
  return false
}

const PianoManager: FunctionComponent = () => {
  const queryClient = useQueryClient()

  const appTheme = useAppTheme()

  const userData = useUser()

  const pianoEvents = usePianoEvents()

  const { subscriptionStatus } = useSubscriptionStatus()

  const {
    targetContentType,
    isPlus,
    isPromo,
    id: articleId,
    contentOrigin = 'blick',
  } = usePageMetadata()

  const isVideoOnly = isVideoOnlyArticle(targetContentType)
  const isLivescore = targetContentType === 'livescore'
  const isDebugMode = useIsPianoDebugModeActive()

  const {
    C0002: arePerformanceCookiesEnabled,
    C0003: areFunctionalCookiesEnabled,
    C0004: areTargetingCookiesEnabled,
  } = useCMPCookieCategories('someCategoriesChanged', [
    'C0002',
    'C0003',
    'C0004',
  ])

  const [cxenseSetupDone, setCxenseSetupDone] = useState(false)
  const [pianoSdkDownloaded, setPianoSdkDownloaded] = useState(false)

  const initialCxenseSetupHasRun = useRef(false)

  const getSSOSource = useCallback(
    () =>
      calculateSSOTrackingSource(
        {
          prefix: 'PIA',
          omitPublication: true,
        },
        queryClient,
      ),
    [queryClient],
  )

  const updatePianoExperience = useCallback<
    Parameters<UseExecuteOnClientNavigation>[0]
  >(
    (metadata) => {
      const {
        targetContentType,
        isPlus,
        isPromo,
        id: articleId,
        contentOrigin = 'blick',
        author,
        breadcrumbs,
        publishedDate,
      } = metadata
      const isVideoOnly = isVideoOnlyArticle(targetContentType)
      const isLivescore = targetContentType === 'livescore'

      // On every route change we have to close templates that were opened
      // by Piano, so that they don't stay open when going back
      // This has to be done on SPA's according to Piano
      window?.tp?.offer?.close?.()

      // For Piano ARI and the dynamic paywall, we need to provide some meta data to Piano
      // They are usually getting it from meta tags, but we are not sure at this point if
      // the DOM and the meta tags are already updated, so we send them the data directly before
      // calling updateExperience
      window?.tp?.push(['setContentAuthor', author])
      window?.tp?.push(['setContentSection', breadcrumbs?.[1]?.title ?? 'Home'])
      window?.tp?.push(['setContentCreated', publishedDate])

      // Update the piano experience every time the page metadata changes
      updateExperience({
        currentUrl: `${window.location.href}`,
        ssoSource: getSSOSource(),
        isPlus: !!isPlus,
        isPromo: !!isPromo,
        isVideoOnly,
        isLivescore,
        queryClient,
        articleId,
        contentOrigin,
      })
    },
    [getSSOSource, queryClient],
  )

  useEffect(() => {
    if (
      !initialCxenseSetupHasRun.current &&
      [
        arePerformanceCookiesEnabled,
        areFunctionalCookiesEnabled,
        areTargetingCookiesEnabled,
      ].every((category) => category !== undefined)
    ) {
      initialCxenseSetupHasRun.current = true
      window.cX?.callQueue.push([
        'setConsent',
        {
          pv: arePerformanceCookiesEnabled,
          segment: arePerformanceCookiesEnabled,
          ad: areTargetingCookiesEnabled,
          recs: areFunctionalCookiesEnabled,
        },
      ])
      setCxenseSetupDone(true)
    }
  }, [
    arePerformanceCookiesEnabled,
    areFunctionalCookiesEnabled,
    areTargetingCookiesEnabled,
  ])

  useEffect(() => {
    // Initialize Piano as soon as the Piano SDK is loaded
    if (
      pianoSdkDownloaded &&
      !queryClient.getQueryData(['isPianoSDKLoaded']) &&
      subscriptionStatus
    ) {
      const ssoSource = getSSOSource()
      const isSubscribed = subscriptionStatus === 'subscribed'
      const isLoggedIn = !!userData

      initializePianoAsync({
        queryClient,
        articleId,
        ssoSource,
        isSubscribed,
        isPlus: !!isPlus,
        isPromo: !!isPromo,
        isLoggedIn,
        isDebugMode,
        isVideoOnly,
        isLivescore,
        pianoEvents,
        contentOrigin,
      })
    }
  }, [
    userData,
    subscriptionStatus,
    pianoEvents,
    isPlus,
    isPromo,
    isVideoOnly,
    isLivescore,
    isDebugMode,
    queryClient,
    pianoSdkDownloaded,
    getSSOSource,
    articleId,
    contentOrigin,
  ])

  useEffect(() => {
    if (appTheme !== 'server') {
      window.tp?.push(['setCustomVariable', 'theme', appTheme])
    }
  }, [appTheme])

  useExecuteOnClientNavigation(updatePianoExperience)

  const onPianoSdkDownloaded = useCallback(() => {
    setPianoSdkDownloaded(true)
  }, [])

  const onPianoSDKErrored = useCallback(() => {
    queryClient.setQueryData(['hasPianoSDKErrored'], true)
    queryClient.invalidateQueries({
      queryKey: ['hasPianoSDKErrored'],
      exact: true,
    })
  }, [queryClient])

  return cxenseSetupDone ? (
    <>
      {!hotModuleReloading && (
        <Script
          id="PianoAdBlockDetection"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s){try{d.cookie='__adblocker=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';var setNptTechAdblockerCookie=function(adblocker){try{var date=new Date();date.setTime(date.getTime()+60*5*1000);d.cookie='__adblocker='+(adblocker?'true':'false')+'; expires='+date.toUTCString()+'; path=/'}catch(err){}};if({}.isAdDisabled){setNptTechAdblockerCookie(true); return};var script=d.createElement('script');script.setAttribute('defer',true);script.setAttribute('src','https://www.npttech.com/advertising.js');script.onerror=function(){if(typeof setNptTechAdblockerCookie==="function"){setNptTechAdblockerCookie(true)}};d.getElementsByTagName('head')[0].appendChild(script)}catch(err){}})(window,document,'script');`,
          }}
        />
      )}
      <Script
        id="PianoARI"
        strategy="afterInteractive"
        defer={true}
        src={pianoARIScriptUrl}
      />
      <Script
        id="PianoSDK"
        strategy="afterInteractive"
        defer={true}
        src={pianoSdkUrl}
        onLoad={onPianoSdkDownloaded}
        onError={onPianoSDKErrored}
      />
    </>
  ) : null
}

export default PianoManager
